import classNames from 'classnames';
import Button from 'components/styleguide/button/Button';
import Icon from 'components/styleguide/icon/Icon';
import IconButton from 'components/styleguide/icon_button/IconButton';
import React, { useState } from 'react';

const styles = require('./Banner.module.scss');

export type BannerVariant = 'neutral' | 'success' | 'info' | 'warning' | 'error';

const VARIANT_DEFAULT_ICON: Partial<Record<BannerVariant, string>> = {
  success: 'circleCheck',
  info: 'info',
  warning: 'circleAlert',
  error: 'triangleAlert',
};

export interface BannerProps {
  buttonLabel?: string;
  className?: string;
  closable?: boolean;
  closeLabel?: string;
  description?: string;
  icon?: string;
  note?: string;
  onButtonClick?: () => void;
  title: string;
  variant?: BannerVariant;
}

const Banner: React.FC<BannerProps> = ({
  buttonLabel,
  className,
  closable = false,
  closeLabel = 'Dismiss',
  description,
  icon,
  note,
  onButtonClick,
  title,
  variant = 'neutral',
}) => {
  const [isClosed, setIsClosed] = useState(false);

  if (isClosed) return null;

  const hasActions = !!buttonLabel || closable;
  const resolvedIcon = icon ?? VARIANT_DEFAULT_ICON[variant];

  return (
    <div
      className={classNames(styles.banner, styles[variant], className)}
      role="status"
    >
      <div className={styles.text}>
        <span className={styles.title}>
          {resolvedIcon && (
            <Icon name={resolvedIcon} size="sm" color={variant === 'neutral' ? 'neutral-border-darker' : `${variant}-border-base`} aria-hidden="true" />
          )}
          {title}
        </span>
        {description && <span className={styles.description}>{description}</span>}
        {note && <span className={styles.note}>{note}</span>}
      </div>
      {hasActions && (
        <div className={styles.actions}>
          {buttonLabel && (
            <Button
              type="primary"
              context="neutral"
              size="S"
              onClick={onButtonClick}
            >
              {buttonLabel}
            </Button>
          )}
          {closable && (
            <IconButton
              iconName="x"
              size="S"
              type="terceary"
              context="neutral"
              aria-label={closeLabel}
              onClick={() => setIsClosed(true)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Banner;
